import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { RInput } from 'components/UI/RInput';
import { RSelectEmail } from 'components/UI/RSelectEmail';
import { RInputMask } from 'components/UI/RInputMask';
import { useRowClickHandler } from 'hooks/useRowClickHandler';
import { useUpdateUserFormData } from 'hooks/useUpdateUserFormData';
import { getInputClass } from 'validation';
import { globalState } from 'store/reducer';
import { arrMails, schema } from './config';
import styles from './styles.module.scss';
import { RSelect } from '../UI/RSelect';
import { months, years } from '../Payment/config';
import {
  encrypt,
  facebookEventLead,
  getMerchant,
  waitRequest,
} from '../Payment/utils';
import axios from 'axios';
import { resAuthorize } from '../../api/resAuthorize';
import { authenticateBrowser } from '../../api/authenticateBrowser';
import { iframeThreeDs, iframeThreeDsChallenge } from '../Payment/templates';
import { updateClick } from '../../api/updateClick';
import {
  getResChallenge,
  getTransactionUpdates,
} from '../../api/getTransactionUpdates';

export const Home = () => {
  const [additionalFormData, setAdditionalFormData] = useState({});
  const [send3dsError, setSend3dsError] = useState(false);
  const updateClickUrl = useSelector(globalState.selectors.updateClickUrl);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { onClickRow } = useRowClickHandler();
  const [isOpenSelects, setIsOpenSelects] = useState(false);
  const [isSelectedEmail, setIsSelectedEmail] = useState(false);
  const [isFirstFormFilled, setIsFirstFormFilled] = useState(false);

  const vocab = useSelector(globalState.selectors.vocab);
  const navigatorLocale = useSelector(globalState.selectors.navigatorLocale);
  const locale = useSelector(globalState.selectors.locale);
  const redirectUrl = useSelector(globalState.selectors.redirectUrl);
  const userForm = useSelector(globalState.selectors.userForm);
  const paymentForm = useSelector(globalState.selectors.paymentForm);
  const merchantUrl = useSelector(globalState.selectors.getMerchantUrl);
  const getFormData = useSelector(globalState.selectors.getFormData);
  const getFullName = useSelector(globalState.selectors.getFullName);
  const baseUrl = useSelector(globalState.selectors.baseUrl);
  const baseUrl3DS = useSelector(globalState.selectors.baseUrl3DS);
  const saveClickUrl = useSelector(globalState.selectors.saveClickUrl);
  const sendClickUrl = useSelector(globalState.selectors.sendClickUrl);

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({
    defaultValues: {
      firstName: userForm.firstName,
      lastName: userForm.lastName,
      email: userForm.email,
      phone: userForm.phone,
      name: paymentForm.name,
      cardNumber: paymentForm.cardNumber,
      expirationMonth: paymentForm.expirationMonth,
      expirationYear: paymentForm.expirationYear,
      cvv: paymentForm.cvv,
    },
    mode: 'all',
    criteriaMode: 'all',
    shouldFocusError: false,
    resolver: yupResolver(schema),
  });
  const data = watch();
  useUpdateUserFormData(data);

  const home = useMemo(
    () => vocab[navigatorLocale].home,
    [vocab, navigatorLocale]
  );
  const mask = useMemo(() => vocab[locale].mask, [vocab, locale]);

  const filteredEmails = useMemo(() => {
    if (data.email.includes('@')) {
      return arrMails.filter(
        (mail) => mail.search(data.email.split('@')[1]) !== -1
      );
    }
    return arrMails;
  }, [data.email]);

  useEffect(() => {
    const firstName = localStorage.getItem('firstName');
    const lastName = localStorage.getItem('lastName');
    const email = localStorage.getItem('email');
    const phone = localStorage.getItem('phone');

    if (firstName && lastName && email && phone) {
      setIsFirstFormFilled(true);
    }

    if (firstName) {
      setValue('firstName', firstName);
    }

    if (lastName) {
      setValue('lastName', lastName);
    }

    if (email) {
      setValue('email', email);
    }

    if (phone) {
      setValue('phone', phone);
    }
  }, []);

  useEffect(() => {
    dispatch(globalState.actions.setPaymentFormData({ name: getFullName }));
    setValue('name', getFullName);
  }, [getFullName]);

  const resetForm = () => {
    setValue('cardNumber', '');
    setValue('expirationMonth', '');
    setValue('expirationYear', '');
    setValue('cvv', '');
    dispatch(
      globalState.actions.setPaymentFormData({
        cardType: 'unknown',
      })
    );
  };

  useEffect(() => {
    let formIsSuccess = true;
    Object.values(userForm).forEach((el) => {
      if (!el) formIsSuccess = false;
    });

    if (formIsSuccess && isFirstFormFilled) {
      schema
        .validate(userForm)
        .then(() => {
          onClickRow(userForm);
          //history.push({ pathname: '/payment', search: location.search });
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [userForm]);

  useEffect(() => {
    const unblock = history.block(() => {
      let isPaymentFailed = window.localStorage.getItem('count') >= 3;

      try {
        if (isPaymentFailed && failedPaymentLink) {
          dispatch(globalState.actions.showLoaderHandler());

          axios
            .post(redirectUrl, {
              ...{ ...getFormData, ...additionalFormData },
              failed_payment_link: failedPaymentLink,
            })
            .then(() => {
              dispatch(globalState.actions.hideLoaderHandler());
              return true;
            })
            .catch(() => {
              dispatch(
                globalState.actions.showPopupHandler(
                  '3D Secure declined, please try again. '
                )
              );

              setTimeout(() => {
                dispatch(globalState.actions.hideLoaderHandler());
                return true;
              }, 2000);
            });
        } else {
          dispatch(globalState.actions.showLoaderHandler());

          setTimeout(() => {
            dispatch(globalState.actions.hideLoaderHandler());
          }, 2000);
          return true;
        }
      } catch (e) {
        console.log(e);
      }
    });

    return () => unblock();
  }, []);

  const showErrorPopup = () => {
    dispatch(globalState.actions.hideLoaderHandler());
    dispatch(
      globalState.actions.showPopupHandler(
        'Error! Unfortunately an error has occurred with the payment! Check that you have entered the details correctly or try again with a different credit card.<br/><br/> Click here and try again!'
      )
    );
  };

  const showErrorPopup3ds = () => {
    dispatch(globalState.actions.hideLoaderHandler());
    dispatch(
      globalState.actions.showPopupHandler(
        '3D Secure declined, please try again.'
      )
    );
  };

  const setCount = () => {
    let count = window.localStorage.getItem('count') || 0;
    count++;
    window.localStorage.setItem('count', count);
    if (count >= 2) {
      history.push({
        pathname: '/',
        search: location.search,
      });
    }
  };

  const saveClick = async (merchantId = null, merchantRuleListId = null) => {
    try {
      const formData = new FormData();
      let ipData;
      try {
        ipData = await axios.get(
          'https://ipgeolocation.abstractapi.com/v1/?api_key=05bca1ff5ec1424b976c4fb50f17c550'
        );
        window.localStorage.setItem('ipAddress', ipData.data.ip_address);
        window.localStorage.setItem('user_country', ipData.data.country);
      } catch (e) {
        console.log('catch ipData');
      }

      const data = {
        ipAddress: ipData.data.ip_address,
        user_country: ipData.data.country,
        fb_pixel: window.localStorage.getItem('fb_pixel'),
        browserData: JSON.stringify({
          acceptHeader: 'application/json',
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: new Date().getTimezoneOffset().toString(),
          colorDepth: screen.colorDepth,
          screen: {
            height: screen.height.toString(),
            width: screen.width.toString(),
          },
          javaScriptEnabled: true,
          javaEnabled: navigator.javaEnabled(),
        }),
      };
      if (merchantId) data['forceMerchantId'] = merchantId;
      if (merchantRuleListId) data['merchantRuleListId'] = merchantRuleListId;

      setAdditionalFormData((prevState) => ({
        ...prevState,
        ...data,
      }));

      formData.append(
        'data',
        encrypt(JSON.stringify({ ...getFormData, ...data }))
      );

      const result = await axios.post(saveClickUrl, formData);

      if (result.data.status === 'ok') {
        window.localStorage.setItem('userClickId', result.data.userCLickId);
        return true;
      }

      Sentry.captureMessage('Save click failed');
      showErrorPopup();
      return false;
    } catch (e) {
      showErrorPopup();
      Sentry.captureMessage(`Save click failed catch: ${e}`);
    }
  };

  const sendForm = async (
    merchantId = null,
    merchantRuleListId = null,
    cavv = null,
    xid = null,
    eci = null,
    cavv_algorithm = null,
    status = null,
    protocolVersion = null,
    cardToken = null
  ) => {
    let handlerError = false;
    try {
      const formData = new FormData();
      let ipData;
      try {
        ipData = await axios.get(
          'https://ipgeolocation.abstractapi.com/v1/?api_key=05bca1ff5ec1424b976c4fb50f17c550'
        );
        window.localStorage.setItem('ipAddress', ipData.data.ip_address);
        window.localStorage.setItem('user_country', ipData.data.country);
      } catch (e) {
        console.log('catch ipData');
      }
      const data = {
        ipAddress: ipData.data.ip_address,
        user_country: ipData.data.country,
        fb_pixel: window.localStorage.getItem('fb_pixel'),
        browserData: JSON.stringify({
          acceptHeader: 'application/json',
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: new Date().getTimezoneOffset().toString(),
          colorDepth: screen.colorDepth,
          screen: {
            height: screen.height.toString(),
            width: screen.width.toString(),
          },
          javaScriptEnabled: true,
          javaEnabled: navigator.javaEnabled(),
        }),
      };
      if (merchantId) data['forceMerchantId'] = merchantId;
      if (merchantRuleListId) data['merchantRuleListId'] = merchantRuleListId;
      if (cavv) data['cavv'] = cavv;
      if (xid) data['xid'] = xid;
      if (eci) data['eci'] = eci;
      if (cavv_algorithm) data['cavv_algorithm'] = cavv_algorithm;
      if (status) data['status'] = status;
      if (protocolVersion) data['protocolVersion'] = protocolVersion;
      if (cardToken) data['cardToken'] = cardToken;

      setAdditionalFormData((prevState) => ({
        ...prevState,
        ...data,
      }));

      formData.append(
        'data',
        encrypt(JSON.stringify({ ...getFormData, ...data }))
      );

      const result = await axios.post(baseUrl, formData);

      if (result.data.status === 'MERC_REDIRECT') {
        eval(result.data.message.script);
      } else {
        const { status, data } = result.data;

        if (status === 'SUCCESS') {
          dispatch(globalState.actions.hideLoaderHandler());
          dispatch(
            globalState.actions.showPopupHandler(
              `Your participation has been confirmed!<br/>The winner of the prize will be contacted by email.<br/>For any questions call support<br/>${data.support_phone}`
            )
          );
          dispatch(globalState.actions.setSuccessPayment());
          if (data.paid) facebookEventLead();
          return false;
        }
        Sentry.captureMessage('Payment failed');
        setCount();
        dispatch(globalState.actions.hideLoaderHandler());
        showErrorPopup3ds();
        handlerError = true;
      }
    } catch (e) {
      dispatch(globalState.actions.hideLoaderHandler());
      showErrorPopup();
    }
    return handlerError;
  };

  const send3DS = async () => {
    let bearer;
    try {
      const resAuthorizeRes = await resAuthorize(baseUrl3DS);
      bearer = resAuthorizeRes.headers.authorization;
    } catch (err) {
      Sentry.captureMessage(`PAAY Authorization error: ${err}`);
      dispatch(globalState.actions.hideLoaderHandler());
      dispatch(
        globalState.actions.showPopupHandler(
          'Something went wrong! Please try again later'
        )
      );
      return false;
    }

    let resAuthenticate;

    try {
      const { data: res } = await authenticateBrowser(
        baseUrl3DS,
        getFormData,
        bearer
      );

      resAuthenticate = res;
      if (send3dsError) {
        setSend3dsError(false);
      }
    } catch (e) {
      setSend3dsError(true);
      dispatch(globalState.actions.hideLoaderHandler());
      setCount();
      Sentry.captureMessage(`PAAY Authenticate browser error: ${e}`);
      dispatch(
        globalState.actions.showPopupHandler(
          'Something went wrong! Please try again later'
        )
      );
      return false;
    }

    if (document.querySelector('#iframe-3ds')) return;

    let iframe = document.createElement('iframe');
    iframe.id = 'iframe-3ds';
    let html = iframeThreeDs(resAuthenticate);
    iframe.src = 'data:text/html;charset=utf-8,' + encodeURI(html);
    document.body.append(iframe);

    const tempFormData = new FormData();
    tempFormData.append(
      'paay_transaction_id',
      encrypt(resAuthenticate.transactionId)
    );
    tempFormData.append(
      'userClickId',
      encrypt(window.localStorage.getItem('userClickId'))
    );
    try {
      await updateClick(updateClickUrl, tempFormData);
    } catch (err) {
      Sentry.captureMessage('Update click error');
    }

    const res = await waitRequest({
      request: () => getTransactionUpdates(baseUrl3DS, resAuthenticate, bearer),
      validator: (res) => res.status < 500,
    });

    const setAdditionalDataByResTransaction = (resTransactionUpdate) => {
      setAdditionalFormData((prevState) => ({
        ...prevState,
        ...prepareDataFrom3DS(resTransactionUpdate),
      }));
    };

    document.querySelector('#iframe-3ds').remove();

    if (res.data.status === 'C') {
      if (document.querySelector('#iframe-3ds-challenge')) return;

      let iframeChallenge = document.createElement('iframe');
      iframeChallenge.id = 'iframe-3ds-challenge';
      let htmlChallenge = iframeThreeDsChallenge(res);
      iframeChallenge.src =
        'data:text/html;charset=utf-8,' + encodeURI(htmlChallenge);
      document.body.append(iframeChallenge);

      const resChallenge = await waitRequest({
        request: () => getResChallenge(baseUrl3DS, resAuthenticate, bearer),
        validator: (res) => res.data.status !== 'C',
      });

      document.querySelector('#iframe-3ds-challenge').remove();

      if (resChallenge === 'error' || res === 'error') {
        setAdditionalFormData((prevState) => ({
          ...prevState,
          cavv: '',
          xid: '',
          eci: '',
          status: '',
          protocolVersion: 'paay_failed',
          cardToken: '',
        }));
        return true;
      }

      const resTransactionUpdate = resChallenge.data;
      setAdditionalDataByResTransaction(resTransactionUpdate);
      return resTransactionUpdate;
    } else {
      const resTransactionUpdate = res.data;
      setAdditionalDataByResTransaction(resTransactionUpdate);
      return resTransactionUpdate;
    }
    // eslint-disable-next-line no-unreachable
    return true;
  };

  const prepareDataFrom3DS = (resTransactionUpdate) => {
    return {
      cavv:
        resTransactionUpdate.authenticationValue !== undefined
          ? resTransactionUpdate.authenticationValue
          : '',
      xid:
        resTransactionUpdate.dsTransId !== undefined
          ? resTransactionUpdate.dsTransId
          : '',
      eci:
        resTransactionUpdate.eci !== undefined ? resTransactionUpdate.eci : '',
      status:
        resTransactionUpdate.status !== undefined
          ? resTransactionUpdate.status
          : '',
      protocolVersion:
        resTransactionUpdate.protocolVersion !== undefined
          ? resTransactionUpdate.protocolVersion
          : '',
      cardToken:
        resTransactionUpdate.cardToken !== undefined
          ? resTransactionUpdate.cardToken
          : '',
    };
  };

  const sendClick = async (
    merchantId = null,
    cavv = null,
    xid = null,
    eci = null,
    cavv_algorithm = null,
    status = null,
    protocolVersion = null,
    cardToken = null
  ) => {
    let handlerError = false;

    try {
      const formData = new FormData();

      const data = {
        userClickId: window.localStorage.getItem('userClickId'),
        browserData: JSON.stringify({
          acceptHeader: 'application/json',
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: new Date().getTimezoneOffset().toString(),
          colorDepth: screen.colorDepth,
          screen: {
            height: screen.height.toString(),
            width: screen.width.toString(),
          },
          javaScriptEnabled: true,
          javaEnabled: navigator.javaEnabled(),
        }),
      };
      if (cavv) data['cavv'] = cavv;
      if (xid) data['xid'] = xid;
      if (eci) data['eci'] = eci;
      if (cavv_algorithm) data['cavv_algorithm'] = cavv_algorithm;
      if (status) data['status'] = status;
      if (protocolVersion) data['protocolVersion'] = protocolVersion;
      if (cardToken) data['cardToken'] = cardToken;
      if (merchantId) data['forceMerchantId'] = merchantId;

      setAdditionalFormData((prevState) => ({
        ...prevState,
        ...data,
      }));

      formData.append(
        'data',
        encrypt(JSON.stringify({ ...getFormData, ...data }))
      );

      const result = await axios.post(sendClickUrl, formData);

      window.localStorage.removeItem('userClickId');

      if (result.data.status === 'MERC_REDIRECT') {
        eval(result.data.message.script);
      } else {
        const { status, data } = result.data;

        if (status === 'SUCCESS') {
          dispatch(globalState.actions.hideLoaderHandler());
          dispatch(
            globalState.actions.showPopupHandler(
              `Your participation has been confirmed!<br/>The winner of the prize will be contacted by email.<br/>For any questions call support<br/>${data.support_phone}`
            )
          );
          dispatch(globalState.actions.setSuccessPayment());
          if (data.paid) facebookEventLead();
          return false;
        }

        Sentry.captureMessage('Payment failed');
        setCount();
        dispatch(globalState.actions.hideLoaderHandler());
        showErrorPopup3ds();
        handlerError = true;
      }
    } catch (e) {
      dispatch(globalState.actions.hideLoaderHandler());
      Sentry.captureMessage(`Payment failed catch: ${e}`);
      showErrorPopup();
    }
    resetForm();
    return handlerError;
  };

  const checkSecurity = async () => {
    const respGetMerchant = await getMerchant(
      paymentForm.cardNumber,
      merchantUrl
    );

    if (!respGetMerchant.status || respGetMerchant.status === 'ERROR') {
      dispatch(globalState.actions.hideLoaderHandler());
      dispatch(
        globalState.actions.showPopupHandler(
          respGetMerchant?.message ||
            'Sorry you can not register with this card. Try to use different card'
        )
      );
      Sentry.captureMessage('Can not register with this card');
      return;
    }

    if (respGetMerchant['paayParams']?.status === 'Y') {
      return await sendForm(
        respGetMerchant.merchantId,
        respGetMerchant.merchantRuleListId,
        respGetMerchant['paayParams']['cavv'],
        respGetMerchant['paayParams']['xid'],
        respGetMerchant['paayParams']['eci'],
        respGetMerchant['paayParams']['cavv_algorithm'],
        respGetMerchant['paayParams']['status'],
        respGetMerchant['paayParams']['protocolVersion'],
        respGetMerchant['paayParams']['cardToken']
      );
    }

    if (respGetMerchant['3ds'] === 'PAAY') {
      if (
        await saveClick(
          respGetMerchant.merchantId,
          respGetMerchant.merchantRuleListId
        )
      ) {
        const res = await send3DS();
        if (res) {
          const preparedRes = prepareDataFrom3DS(res);
          await sendClick(
            respGetMerchant.merchantId,
            preparedRes.cavv,
            preparedRes.xid,
            preparedRes.eci,
            preparedRes.cavv_algorithm,
            preparedRes.status,
            preparedRes.protocolVersion,
            preparedRes.cardToken
          );
        }
      }
    }

    if (respGetMerchant['3ds'] === 'DIRECT') {
      if (
        await saveClick(
          respGetMerchant.merchantId,
          respGetMerchant.merchantRuleListId
        )
      ) {
        await sendClick(respGetMerchant.merchantId);
      }
    }
  };

  const onSubmit = async () => {
    onClickRow(userForm);
    Sentry.captureMessage('Send form');
    dispatch(globalState.actions.showLoaderHandler());
    await checkSecurity();
    //history.push({ pathname: '/payment', search: location.search });
  };

  const updateEmail = (email) => {
    setValue('email', email);
    trigger('email');
    setIsOpenSelects(false);
    setIsSelectedEmail(true);
  };

  useEffect(() => {
    if (!isSelectedEmail) {
      if (
        data.email.includes('@') &&
        !arrMails.includes(userForm.email.split('@')[1]) &&
        filteredEmails.length > 0
      ) {
        if (!isOpenSelects && dirtyFields.email) {
          setIsOpenSelects(true);
        }
      } else {
        if (isOpenSelects) {
          setIsOpenSelects(false);
        }
      }
    }
  }, [data.email, filteredEmails]);

  const mainTexts = useMemo(
    () => vocab[navigatorLocale].mainTexts,
    [vocab, navigatorLocale]
  );

  const onHandleInputEmail = () => {
    if (isSelectedEmail) {
      setIsSelectedEmail(false);
    }
  };

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={styles.formTitle}>{mainTexts.formTitle1}</div>
      <div className={styles.box}>
        <div className={styles.inputField}>
          <div className={styles.label}>{home.placeholder.firstName}</div>
          <RInput
            name="firstName"
            //placeholder={home.placeholder.firstName}
            containerClassNames={styles.input}
            inputClassNames={`input-field ${getInputClass(
              'firstName',
              data.firstName,
              errors,
              schema
            )}`}
            register={register}
          />
        </div>
        <div className={styles.inputField}>
          <div className={styles.label}>{home.placeholder.lastName}</div>
          <RInput
            name="lastName"
            // placeholder={home.placeholder.lastName}
            containerClassNames={styles.input}
            inputClassNames={`input-field ${getInputClass(
              'lastName',
              data.lastName,
              errors,
              schema
            )}`}
            register={register}
          />
        </div>

        <div className={styles.inputField}>
          <div className={styles.label}>{home.placeholder.address}</div>
          <RInput
            name="address"
            //placeholder={home.placeholder.firstName}
            containerClassNames={styles.input}
            inputClassNames={`input-field ${getInputClass(
              'address',
              data.address,
              errors,
              schema
            )}`}
            register={register}
          />
        </div>

        <div className={styles.inputField}>
          <div className={styles.label}>
            {home.placeholder.zip}
            {/*{home.placeholder.firstName}*/}
          </div>
          <RInput
            name="zip"
            //placeholder={home.placeholder.firstName}
            containerClassNames={styles.input}
            inputClassNames={`input-field ${getInputClass(
              'zip',
              data.zip,
              errors,
              schema
            )}`}
            register={register}
          />
        </div>

        <div className={styles.inputField}>
          <div className={styles.label}>{mainTexts.countryLabel}</div>
          <div className={styles.input}>
            <input
              type="text"
              className={styles.inputReadOnly}
              defaultValue={
                localStorage.getItem('countryName')
                  ? localStorage.getItem('countryName')
                  : mainTexts.country
              }
              readOnly={true}
            />
          </div>
        </div>

        <div className={styles.inputField}>
          <div className={styles.label}>{home.placeholder.city}</div>
          <RInput
            name="city"
            //placeholder={home.placeholder.firstName}
            containerClassNames={styles.input}
            inputClassNames={`input-field ${getInputClass(
              'city',
              data.city,
              errors,
              schema
            )}`}
            register={register}
          />
        </div>

        <div className={styles.inputField}>
          <div className={styles.label}>{home.placeholder.phone}</div>
          <RInputMask
            name="phone"
            inputMode="numeric"
            value={userForm.phone}
            containerClassNames={styles.input}
            mask={mask.phone}
            //placeholder={home.placeholder.phone}
            inputClassNames={`${
              styles[`form__inp`]
            } input-field input-field--icon-prepend ${getInputClass(
              'phone',
              data.phone,
              errors,
              schema
            )}`}
            prependIcon={
              <span
                className={`${styles[`form__inp-icon-country`]} ${
                  styles[locale]
                }`}
              />
            }
            register={register}
          />
        </div>

        <div className={styles.inputField}>
          <div className={styles.label}>{home.placeholder.email}</div>
          <RSelectEmail
            currentEmail={data.email}
            isOpenSelects={isOpenSelects}
            containerClassNames={styles.input}
            arrMails={filteredEmails}
            setEmail={updateEmail}
            inputClassNames={`input-field ${getInputClass(
              'email',
              data.email,
              errors,
              schema
            )}`}
            //placeholder={home.placeholder.email}
            register={register}
            onInput={onHandleInputEmail}
          />
        </div>
      </div>

      <div className={styles.formTitle}>{mainTexts.formTitle2}</div>
      <div className={styles.box}>
        <div className={styles.inputField}>
          <div className={styles.label}>{home.cardNumber}</div>
          <RInputMask
            id="card-number"
            inputMode="numeric"
            name="cardNumber"
            placeholder="•••• •••• •••• ••••"
            mask={mask.creditCard}
            value={data.cardNumber}
            containerClassNames={styles.input}
            inputClassNames={getInputClass(
              'cardNumber',
              data.cardNumber,
              errors,
              schema
            )}
            register={register}
          />
        </div>

        <div className={styles.double}>
          <div className={styles.inputField}>
            <div className={styles.label}>{home.month}</div>
            <RSelect
              id="expiration-month"
              name="expirationMonth"
              firstElement={home.month}
              containerClassNames={styles.input}
              selectClassNames={`${
                styles[`form-payment__inp-month`]
              } ${getInputClass(
                'expirationMonth',
                data.expirationMonth,
                errors,
                schema
              )}`}
              options={months}
              register={register}
            />
          </div>

          <div className={styles.inputField}>
            <div className={styles.label}>{home.year}</div>
            <RSelect
              id="expiration-year"
              name="expirationYear"
              firstElement={home.year}
              containerClassNames={styles.input}
              selectClassNames={`${
                styles[`form-payment__inp-year`]
              } ${getInputClass(
                'expirationYear',
                data.expirationYear,
                errors,
                schema
              )}`}
              options={years}
              register={register}
            />
          </div>
        </div>

        <div className={styles.inputField}>
          <div className={styles.label}>{home.name}</div>
          <RInput
            id="name"
            name="name"
            placeholder={home.placeholder.name}
            containerClassNames={styles.input}
            inputClassNames={getInputClass('name', data.name, errors, schema)}
            register={register}
          />
        </div>

        <div className={styles.inputField}>
          <div className={styles.label}>{home.cvv}</div>
          <RInputMask
            id="cvv"
            inputMode="numeric"
            name="cvv"
            placeholder="•••"
            mask={mask.cvv}
            value={data.cvv}
            containerClassNames={`${styles.input} ${
              styles[`form-payment__row--w50`]
            }`}
            inputClassNames={`${styles[`only-number`]} ${getInputClass(
              'cvv',
              data.cvv,
              errors,
              schema
            )}`}
            register={register}
          />
        </div>
      </div>

      <div className={styles[`form__row`]}>
        <button
          id="btn-form-submit"
          type="submit"
          className={styles[`form__btn-submit`]}
        >
          {mainTexts.btnForm2}
        </button>
      </div>
    </form>
  );
};
